/**
 * GoogleCalendarPicker — visa anslutna Google-konton, deras kalendrar,
 * och låt user välja vilka som ska synkas in i Gemensam kalender.
 *
 * Mounteras i SettingsPage Google-sektionen.
 */
import { useState, useEffect, useCallback } from "react";
import { CalendarDays, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export function GoogleCalendarPicker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/google/calendar/list");
      setData(data);
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error("Kunde inte hämta kalendrar — kanske behövs återanslutning?");
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleCalendar = async (tokenId, calId, currentlyOn) => {
    if (!data) return;
    // Beräkna ny lista
    const allEnabled = new Set();
    for (const acc of data.accounts) {
      for (const c of acc.calendars) {
        const key = `${acc.token_id}::${c.id}`;
        if (acc.token_id === tokenId && c.id === calId) {
          if (!currentlyOn) allEnabled.add(key);
        } else if (c.enabled) {
          allEnabled.add(key);
        }
      }
    }
    setSavingPrefs(true);
    try {
      await api.put("/google/calendar/preferences", {
        enabled_calendar_ids: [...allEnabled],
      });
      // Optimistic update istället för full reload
      setData((d) => ({
        ...d,
        accounts: d.accounts.map((a) =>
          a.token_id === tokenId
            ? {
                ...a,
                calendars: a.calendars.map((c) =>
                  c.id === calId ? { ...c, enabled: !currentlyOn } : c,
                ),
              }
            : a,
        ),
      }));
    } catch (_) {
      toast.error("Kunde inte spara val");
    } finally {
      setSavingPrefs(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/google/calendar/sync");
      toast.success(
        `Synkat: ${data.inserted} nya, ${data.updated} uppdaterade, ${data.deleted} borttagna`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Synk misslyckades");
    } finally {
      setSyncing(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="text-xs text-slate-500 flex items-center gap-2">
        <RefreshCw size={12} className="animate-spin" /> Laddar kalendrar…
      </div>
    );
  }
  if (!data || !data.configured) {
    return (
      <div
        data-testid="google-calendar-picker"
        className="text-xs text-amber-700 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-2"
      >
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>
          Inga Google-konton anslutna än, eller så saknar befintliga konton
          <strong> Calendar-permissions</strong> — koppla bort och anslut på nytt
          för att aktivera kalender-synk.
        </span>
      </div>
    );
  }

  // Räkna enabled kalendrar
  const totalEnabled = data.accounts.reduce(
    (sum, a) => sum + a.calendars.filter((c) => c.enabled).length, 0,
  );

  return (
    <section data-testid="google-calendar-picker" className="space-y-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays size={16} className="text-indigo-600" />
          <h4 className="text-sm font-semibold">Google Calendar-synk</h4>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
            {totalEnabled} kalendrar
          </span>
        </div>
        <button
          type="button"
          onClick={syncNow}
          disabled={syncing}
          data-testid="gcal-sync-now"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold bg-indigo-600 text-white shadow-sm border border-indigo-700 hover:bg-indigo-700 disabled:opacity-60"
        >
          {syncing
            ? <RefreshCw size={12} className="animate-spin text-white" />
            : <RefreshCw size={12} className="text-white" />}
          <span className="text-white">{syncing ? "Synkar…" : "Synka nu"}</span>
        </button>
      </header>

      <p className="text-[11px] text-slate-500">
        Välj vilka kalendrar som ska synas i Gemensam kalender. Autom. synk var
        15:e minut. Endast läsbehörighet — appen ändrar aldrig i Google.
      </p>

      <div className="space-y-3">
        {data.accounts.map((acc) => (
          <div key={acc.token_id} className="border border-slate-200 rounded-md p-2.5 bg-white">
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-medium text-xs text-slate-700">
                {acc.label || acc.email}
              </div>
              <div className="text-[10px] text-slate-400">{acc.email}</div>
            </div>
            {acc.error && (
              <div className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded p-1.5 mb-2">
                {acc.error} — behöver återanslutas för Calendar-access
              </div>
            )}
            {acc.calendars.length === 0 && !acc.error && (
              <div className="text-[11px] text-slate-400 italic">Inga kalendrar hittade</div>
            )}
            <ul className="space-y-1">
              {acc.calendars.map((c) => (
                <li key={c.id} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={c.enabled}
                    disabled={savingPrefs}
                    onChange={() => toggleCalendar(acc.token_id, c.id, c.enabled)}
                    data-testid={`gcal-toggle-${acc.token_id}-${c.id}`}
                    className="rounded border-slate-300"
                  />
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: c.color }}
                  />
                  <span className="flex-1 truncate">{c.summary}</span>
                  {c.primary && (
                    <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-indigo-100 text-indigo-700">
                      Primär
                    </span>
                  )}
                  {c.enabled && (
                    <CheckCircle2 size={12} className="text-emerald-500 shrink-0" />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
